import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import { clusterApiUrl } from "@solana/web3.js";
import "@solana/wallet-adapter-react-ui/styles.css";

/* ------------------------------------------------------------------ */
/* Network registry — the ONLY place networks are defined.            */
/* Token creation & payment services import from here, so switching   */
/* environments never requires touching the on-chain code paths.      */
/* ------------------------------------------------------------------ */
export const DEFAULT_LOCALNET_RPC =
  process.env.REACT_APP_SOLANA_LOCALNET_RPC || "http://127.0.0.1:8899";

export const NETWORKS = {
  LOCALNET: {
    id: "LOCALNET",
    label: "Localnet",
    shortLabel: "LOCALNET TEST",
    // NOTE: this is the fallback only. Effective RPC is resolved at runtime
    // via useStickyNetwork().network.rpc (Custom RPC URL from Settings).
    rpc: DEFAULT_LOCALNET_RPC,
    adapter: WalletAdapterNetwork.Devnet, // wallets treat localnet as devnet
    explorerCluster: "custom",
    disabled: false,
    dotClass: "is-local",
  },
  DEVNET: {
    id: "DEVNET",
    label: "Devnet",
    shortLabel: "DEVNET TEST",
    rpc: process.env.REACT_APP_SOLANA_RPC || clusterApiUrl(WalletAdapterNetwork.Devnet),
    adapter: WalletAdapterNetwork.Devnet,
    explorerCluster: "devnet",
    disabled: false,
    dotClass: "is-dev",
  },
  MAINNET: {
    id: "MAINNET",
    label: "Mainnet",
    shortLabel: "MAINNET (Disabled)",
    rpc: clusterApiUrl(WalletAdapterNetwork.Mainnet),
    adapter: WalletAdapterNetwork.Mainnet,
    explorerCluster: "mainnet-beta",
    disabled: true, // Phase 3 rule: Mainnet is off
    dotClass: "is-main",
  },
};

const DEFAULT_NETWORK_ID = (process.env.REACT_APP_SOLANA_DEFAULT_NETWORK || "DEVNET").toUpperCase();
const LS_KEY = "sticky_launchpad_network";
const LOCALNET_RPC_LS_KEY = "sticky_launchpad_localnet_rpc";

export function isValidRpcUrl(url) {
  const s = String(url || "").trim();
  if (!/^https?:\/\//i.test(s)) return false;
  try { new URL(s); return true; } catch { return false; }
}

const NetworkContext = createContext({
  network: NETWORKS[DEFAULT_NETWORK_ID],
  setNetworkId: () => {},
  localnetRpc: DEFAULT_LOCALNET_RPC,
  setLocalnetRpc: () => ({ ok: false }),
});

export const useStickyNetwork = () => useContext(NetworkContext);

export function buildExplorerUrl(kind, id, network) {
  const cluster = network.explorerCluster;
  const base = `https://explorer.solana.com/${kind}/${id}`;
  if (cluster === "custom") {
    const custom = encodeURIComponent(network.rpc);
    return `${base}?cluster=custom&customUrl=${custom}`;
  }
  if (cluster === "mainnet-beta") return base;
  return `${base}?cluster=${cluster}`;
}

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */
export function StickyWalletProvider({ children }) {
  const [networkId, setNetworkIdState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_NETWORK_ID;
    const saved = window.localStorage.getItem(LS_KEY);
    if (saved && NETWORKS[saved] && !NETWORKS[saved].disabled) return saved;
    return DEFAULT_NETWORK_ID;
  });

  const [localnetRpc, setLocalnetRpcState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LOCALNET_RPC;
    const saved = window.localStorage.getItem(LOCALNET_RPC_LS_KEY);
    return isValidRpcUrl(saved) ? saved : DEFAULT_LOCALNET_RPC;
  });

  const setNetworkId = (id) => {
    const upper = String(id || "").toUpperCase();
    const net = NETWORKS[upper];
    if (!net || net.disabled) return;
    setNetworkIdState(upper);
    try { window.localStorage.setItem(LS_KEY, upper); } catch { /* ignore */ }
  };

  const setLocalnetRpc = (url) => {
    const clean = String(url || "").trim().replace(/\/+$/, "");
    if (!isValidRpcUrl(clean)) {
      return { ok: false, error: "Enter a valid http(s) URL, e.g. https://xxx.ngrok-free.dev" };
    }
    setLocalnetRpcState(clean);
    try { window.localStorage.setItem(LOCALNET_RPC_LS_KEY, clean); } catch { /* ignore */ }
    return { ok: true };
  };

  const network = useMemo(() => {
    const base = NETWORKS[networkId] || NETWORKS.DEVNET;
    if (base.id === "LOCALNET") return { ...base, rpc: localnetRpc };
    return base;
  }, [networkId, localnetRpc]);

  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter({ network: network.adapter }),
    ],
    // Rebuild adapters when the adapter network changes (Localnet + Devnet share Devnet adapter)
    [network.adapter]
  );

  const ctxValue = useMemo(
    () => ({ network, setNetworkId, localnetRpc, setLocalnetRpc }),
    [network, localnetRpc]
  );

  useEffect(() => {
    try { window.localStorage.setItem(LS_KEY, networkId); } catch { /* ignore */ }
  }, [networkId]);

  const connectionConfig = useMemo(() => ({ commitment: "confirmed" }), []);

  return (
    <NetworkContext.Provider value={ctxValue}>
      {/*
        key={network.rpc} forces a full remount of ConnectionProvider (and
        all descendants) whenever the effective RPC URL changes. This
        guarantees:
          - the previous @solana/web3.js Connection instance is dropped
          - its rpc-websockets client (max_reconnects: Infinity) is GC'd,
            so no lingering WS retries against a stale endpoint.
        Wallet re-connects automatically via autoConnect.
      */}
      <ConnectionProvider key={network.rpc} endpoint={network.rpc} config={connectionConfig}>
        <WalletProvider
          wallets={wallets}
          autoConnect
          onError={(err) => {
            if (typeof console !== "undefined")
              console.warn("Wallet error:", err?.name || err?.message || "unknown");
          }}
        >
          <WalletModalProvider>{children}</WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </NetworkContext.Provider>
  );
}
